import { useMemo } from "react";

import Box from "@/components/Box";
import { ExternalTextLink } from "@/components/Links";
import { useOrganisationContext } from "@/contexts/organisations/OrganisationContext";
import { theme } from "@/core/theme";
import { CMP_PATIENTS_TEST_RESULT_URL, getCmpUrl } from "@/core/urls";
import { PanelBoxV2 } from "@/tpo/Boxes";
import ChevronComponent from "@/tpo/Chevron";
import { List, ListContext } from "@/tpo/List";
import SimpleTable from "@/tpo/SimpleTable";
import Spacer from "@/tpo/Spacer";
import Stack from "@/tpo/Stack";
import Table from "@/tpo/Table";
import isProxyEmail from "@/utils/isProxyEmail";
import ButtonV2 from "@/v2/Buttons";

import NewResultsReviewList from "./NewResultsReviewList";

export default function NewResultsAvailable({
  userTests,
  loading,
  pageInfo,
  search,
  setSearch,
  sort,
  setSort,
  sortMenuOpen,
  setSortMenuOpen,
  setPage,
  sortingOptions
}) {
  const { organisation } = useOrganisationContext();
  const organisationId = organisation?.hashId;

  const columns = useMemo(
    () => [
      {
        id: "patient",
        label: "Patient",
        accessor: userTest => (
          <Stack gap={5}>
            <Box fontFamily="gilroyBold" fontSize={16}>
              {userTest.user.fullName}
            </Box>
            {!isProxyEmail(userTest.user.email) ? (
              <Box fontFamily="gilroyMedium" fontSize={14} wordWrap="anywhere">
                {userTest.user.email}
              </Box>
            ) : null}
          </Stack>
        )
      },
      {
        id: "test",
        label: "Test",
        accessor: userTest => (
          <Stack gap={5}>
            <Box fontFamily="gilroyBold" fontSize={16}>
              {userTest.testProduct?.name}
            </Box>
            {userTest.orderTestItem?.providerTestId ? (
              <Box fontFamily="gilroyMedium" fontSize={14}>
                Kit registration: {userTest.orderTestItem.providerTestId}
              </Box>
            ) : null}
          </Stack>
        )
      },
      {
        id: "action",
        label: "",
        align: "right",
        width: "20%",
        accessor: userTest => (
          <ButtonV2
            color="green"
            rightIcon={<ChevronComponent />}
            as={ExternalTextLink}
            underline={false}
            href={getCmpUrl(CMP_PATIENTS_TEST_RESULT_URL, {
              patientId: userTest.user.id,
              userTestId: userTest.id,
              organisationId
            })}
            size="md"
            sx={{ ml: "auto" }}
          >
            review
          </ButtonV2>
        )
      }
    ],
    [organisationId]
  );

  return (
    <PanelBoxV2 maxWidth={1280}>
      <PanelBoxV2
        maxWidth={1020}
        outer={{
          pt: theme.spacing.section.pt,
          pb: theme.spacing.section.pb,
          bg: "white",
          px: 20,
          borderRadius: 5,
          width: "100%"
        }}
      >
        <Box fontFamily="gilroyBold" fontSize={[28, 28, 36]}>
          New Results Available
        </Box>
        <Spacer py={2} />
        <Box fontFamily="gilroyMedium" fontSize={[14, 14, 16]}>
          Below are a list of new results from your clients that require you to review and release.
        </Box>
        <Spacer py={[15, 15, 20]} />
        <SimpleTable
          search={search}
          setSearch={setSearch}
          sort={sort}
          setSort={setSort}
          sortMenuOpen={sortMenuOpen}
          setSortMenuOpen={setSortMenuOpen}
          pageInfo={pageInfo}
          setPage={setPage}
          sortingOptions={sortingOptions}
          table={
            <>
              <Box display={["none", "none", "block"]}>
                <Table
                  columns={columns}
                  loading={loading}
                  rows={userTests}
                  emptyMessage="No new results available"
                />
              </Box>
              <List items={userTests} loading={loading} display={["block", "block", "none"]}>
                <ListContext.Consumer>
                  {items => <NewResultsReviewList userTests={items} loading={loading} />}
                </ListContext.Consumer>
              </List>
            </>
          }
        />
      </PanelBoxV2>
    </PanelBoxV2>
  );
}
